import { DateTime } from 'luxon'
import { Session } from 'next-auth'
import { getSession, signOut, useSession } from 'next-auth/react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useContext, useEffect, useState } from 'react'
import { Accordion, Button } from 'react-bootstrap'

import { BiDollar, BiSupport } from 'react-icons/bi'
import {
  BsBoxArrowRight,
  BsBullseye,
  BsCalendar2Date,
  BsCalendar3,
  BsCalendarEvent,
  BsCardList,
  BsCashCoin,
  BsClipboardData,
  BsFilePerson,
  BsFillCloudyFill,
  BsFillFileEarmarkArrowUpFill,
  BsFunnel,
  BsGear,
  BsMegaphone,
  BsPeople,
  BsPerson,
  BsFillFileEarmarkTextFill,
  BsStars,
  BsGrid,
} from 'react-icons/bs'
import {
  FaBarcode,
  FaCashRegister,
  FaChartPie,
  FaFileInvoiceDollar,
  FaList,
  FaMedal,
  FaMoneyBillWave,
  FaMotorcycle,
} from 'react-icons/fa'
import { GiRoundTable } from 'react-icons/gi'
import { SiIfood } from 'react-icons/si'
import { FaFileInvoice } from 'react-icons/fa6'
import { GoPackage } from 'react-icons/go'
import { ImCogs, ImLink, ImTicket } from 'react-icons/im'
import { IoTicketOutline } from 'react-icons/io5'
import { MdVideoSettings } from 'react-icons/md'
import { RiRestaurantLine, RiRobot2Line } from 'react-icons/ri'
import { AppContext } from '../../context/app.ctx'
import { updates } from '../../utils/update'
import { textPackage } from '../../utils/wm-functions'
import { useWhatsAppBot } from '@hooks/useWhatsAppBot'
import i18n from '@whatsmenu/i18n'
import {
  IS_TAWKTO_ENABLED,
  SUPPORT_WHATSAPP_URL,
  toggleTawkTo,
} from '@components/TawkTo'

interface ISessionUser extends Session {
  user: any
}

interface Profile {
  timezone: string
}

const isAiMenuAvailable = (value?: string | boolean | null) => {
  if (typeof value === 'boolean') return value

  if (typeof value !== 'string') return false

  const time = Date.parse(value)

  return !Number.isNaN(time) && new Date(time).getTime() > Date.now()
}

export function Sidebar({ compact = false }: { compact?: boolean }) {
  const { t } = i18n
  const {
    invoicePending,
    profile,
    plansCategory,
    handleConfirmModal,
    changeConfig,
    setChangeConfig,
    setShowSidebar,
    bartenders,
    user,
    lowStockAlert,
    setShowNewFeatureModal,
  } = useContext(AppContext)
  const { data: session } = useSession()
  const { canUseWhatsMenuDesktop: canUseWhatsmenuDesktop } = useWhatsAppBot()
  const firstInvoiceIsOverdue =
    DateTime.fromISO(
      invoicePending?.invoices[0]?.requests.toReversed()[0]?.limit
    ).diffNow('days').days < 0

  const invoiceAlert = firstInvoiceIsOverdue ? 'danger' : 'warning'

  const isNFCe = Boolean(profile?.options?.integrations?.grovenfe)

  const router = useRouter()

  useEffect(() => {
    const links = document.querySelectorAll<HTMLElement>(
      '#sidebar-nav .nav-link'
    )

    links.forEach((link) => {
      if (compact) {
        link.title = link.textContent?.trim().replace(/\s+/g, ' ') || ''
      } else {
        link.removeAttribute('title')
      }
    })
  }, [compact, profile.id, plansCategory])

  // 1. Garante que existe pelo menos uma atualização na lista
  const hasUpdates = Array.isArray(updates) && updates.length > 0

  // 2. Obtém a data do update mais recente
  const latestUpdateDate = hasUpdates ? updates[0].createdAt : ''

  // 3. Estado local para o last_seen_update para forçar o React a re-renderizar a Sidebar
  const [lastSeenISO, setLastSeenISO] = useState<string | null>(() => {
    return typeof window !== 'undefined'
      ? localStorage.getItem('last_seen_update')
      : null
  })

  // 4. É um novo update se houver post E (o usuário nunca viu OU o post atual é mais recente)
  const isNewUpdate =
    hasUpdates &&
    (!lastSeenISO ||
      DateTime.fromISO(latestUpdateDate) > DateTime.fromISO(lastSeenISO))

  // 5. Cor do alerta
  const updateAlert = isNewUpdate ? 'danger' : ''

  // 6. EFEITO: Redirecionamento e marcação temporizada de visualização
  useEffect(() => {
    const isOrdersPage =
      router.pathname === '/dashboard/request' ||
      router.pathname === '/dashboard'

    // Cenário A: Se está na página de Pedidos/Dashboard e há novo update -> Redireciona
    if (isNewUpdate && isOrdersPage) {
      router.push('/dashboard/updates')
      return
    }

    // Cenário B: Se está na página de Novidades -> Exibe o destaque e remove após 3.5 segundos
    if (
      router.pathname === '/dashboard/updates' &&
      isNewUpdate &&
      latestUpdateDate
    ) {
      // Salva no localStorage imediatamente para garantir a persistência
      localStorage.setItem('last_seen_update', latestUpdateDate)

      // Aguarda 15 segundos com o destaque ativo antes de remover a estilização/animação
      const timer = setTimeout(() => {
        setLastSeenISO(latestUpdateDate)
        updateAlert
      }, 15000)

      // Cleanup: Cancela o timer se o componente for desmontado antes do tempo
      return () => clearTimeout(timer)
    }
  }, [isNewUpdate, latestUpdateDate, router, router.pathname, updateAlert])

  // 7. Função de clique manual
  const handleNewsClick = (e: any) => {
    if (hasUpdates && latestUpdateDate) {
      localStorage.setItem('last_seen_update', latestUpdateDate)
      setLastSeenISO(latestUpdateDate)
    }
    verificationStateRouter(e)
  }

  /**
   * Retorna uma string representando o horário de funcionamento das 8h às 20h30
   * no fuso horário fornecido.
   *
   * @param {string} timezone - Cadeia de caracteres do fuso horário IANA
   * @returns {string} - String formatada representando o horário de funcionamento
   */
  const businessHours = (timezone: string): string => {
    const formattedOpeningTime = DateTime.local()
      .set({ hour: 8, minute: 0 })
      .setZone(timezone)
      .toFormat('HH:mm')

    const formattedClosingTime = DateTime.local()
      .set({ hour: 21, minute: 0 })
      .setZone(timezone)
      .toFormat('HH:mm')
    return `${formattedOpeningTime} às ${formattedClosingTime}`
  }

  const timezone = profile?.timeZone || 'America/Sao_Paulo'
  const formattedHours = businessHours(timezone)

  const verificationStateRouter = useCallback(
    (e: any, logout?: boolean) => {
      window.innerWidth < 1020 && setShowSidebar(false)

      if (changeConfig?.changeState) {
        e.preventDefault()
        const target = e.target.nodeName === 'SPAN' ? 'currentTarget' : 'target'
        const urlRedirect = e[target].href.substr(
          e[target].href.indexOf('/dashboard')
        )
        handleConfirmModal({
          show: true,
          confirmButton: 'Salvar',
          cancelButton: 'Descartar',
          title: 'Alterações Pendentes',
          message: 'Existem alterações não salvas, deseja salvar?',
          actionConfirm: () => {
            setChangeConfig({
              changeState: true,
              confirmSave: true,
              toRouter() {
                setChangeConfig({})
                logout ? signOut() : router.push(urlRedirect)
              },
            })
          },
          actionCancel: () => {
            setChangeConfig({})
            logout ? signOut() : router.push(urlRedirect)
          },
        })
      } else if (logout) {
        signOut()
      }
    },
    [router, handleConfirmModal, changeConfig, setChangeConfig, setShowSidebar]
  )

  return (
    <>
      <ul
        className={`sidebar-nav ${compact ? 'sidebar-nav-compact' : ''}`}
        id="sidebar-nav"
      >
        {profile.id &&
          ((profile.address.street && profile.taxDelivery.length > 0) ||
            plansCategory.every((plan) => plan === 'table')) && (
            <>
              {/* <li className="nav-item">
              <Link href="/dashboard">
                <Link className="with-icon nav-link collapsed"
                  onClick={e => {
                    verificationStateRouter(e);
                  }}>
                  <BsGrid />
                  <span>Dashboard</span>
                </Link>
              </Link>
            </li> */}
            <li className="nav-item">
              <Link
                href="/dashboard"
                className="with-icon nav-link collapsed"
                onClick={(e) => {
                  verificationStateRouter(e)
                }}
              >
                <BsGrid />
                <span>Painel de controle</span>
              </Link>
            </li>
              <li className="nav-item">
                <Link
                  href="/dashboard/request"
                  className="with-icon nav-link collapsed"
                  onClick={(e) => {
                    verificationStateRouter(e)
                  }}
                >
                  <ImTicket />
                  <span>{t('orders')}</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  href="/dashboard/settings/cashback"
                  className="with-icon nav-link collapsed"
                  onClick={(e) => {
                    verificationStateRouter(e)
                    window.innerWidth < 1020 && setShowSidebar(false)
                  }}
                >
                  <BsCashCoin />
                  <span>Cashback</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  href="/dashboard/integrations/ifood"
                  className="with-icon nav-link collapsed justify-content-between"
                  onClick={(e) => {
                    verificationStateRouter(e)
                    window.innerWidth < 1020 && setShowSidebar(false)
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <SiIfood />
                    <span>MotoBoy IFOOD</span>
                  </div>
                  <span className="badge bg-danger ms-auto inline">
                    {t('new')}
                  </span>
                </Link>
              </li>

              {canUseWhatsmenuDesktop && (
                <li className="nav-item">
                  <Link
                    href="/dashboard/integrations/ifood"
                    className="with-icon nav-link collapsed justify-content-between"
                    onClick={(e) => {
                      verificationStateRouter(e)
                      window.innerWidth < 1020 && setShowSidebar(false)
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <SiIfood />
                      <span>IFOOD</span>
                    </div>
                  </Link>
                </li>
              )}
              {i18n.language === 'pt-BR' && (
                <>
                  <li className="nav-item">
                    <Link
                      href="/dashboard/integrations/keeta"
                      className="with-icon nav-link collapsed justify-content-between"
                      onClick={(e) => {
                        verificationStateRouter(e)
                        window.innerWidth < 1020 && setShowSidebar(false)
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <Image
                          src="/images/keeta/keeta-logo2.png"
                          width={21}
                          height={16}
                          alt="KEETA"
                        />
                        <span>KEETA</span>
                      </div>
                      <span className="badge bg-danger ms-3 inline">Novo</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      href="/dashboard/integrations/99food"
                      className="with-icon nav-link collapsed justify-content-between"
                      onClick={(e) => {
                        verificationStateRouter(e)
                        window.innerWidth < 1020 && setShowSidebar(false)
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <Image
                          src="/images/nine-nine-food/99food-logo.svg"
                          width={21}
                          height={16}
                          alt="99Food"
                        />
                        <span>99FOOD</span>
                      </div>
                      <span className="badge bg-danger ms-3 inline">Novo</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      href="/dashboard/integrations/grovenfe"
                      className="with-icon nav-link collapsed justify-content-between pulse-success"
                      onClick={(e) => {
                        verificationStateRouter(e)
                        window.innerWidth < 1020 && setShowSidebar(false)
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <FaFileInvoice />
                        <span>Emissão de notas</span>
                      </div>
                    </Link>
                  </li>
                </>
              )}
              <li className="nav-item">
                <Link
                  href="/dashboard/integrations"
                  className="with-icon nav-link collapsed"
                  onClick={(e) => {
                    verificationStateRouter(e)
                    window.innerWidth < 1020 && setShowSidebar(false)
                  }}
                >
                  <ImLink />
                  <span>{t('integrations')}</span>
                  {/* <span className="badge bg-danger ms-auto inline">
                    {t('new')}
                  </span> */}
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  href="/dashboard/menu"
                  className="with-icon nav-link collapsed"
                  onClick={(e) => {
                    verificationStateRouter(e)
                  }}
                >
                  <BsCardList />
                  <span>{t('menu')}</span>
                  {lowStockAlert() && (
                    <span
                      style={{ left: '2px' }}
                      className="position-relative translate-middle bg-warning border-warning rounded-circle border p-1"
                    >
                      <span className="visually-hidden">{t('stock')}</span>
                    </span>
                  )}
                </Link>
              </li>
              {(session?.user?.admMode || isAiMenuAvailable(session?.user?.controls?.ai)) && (
                <li className="nav-item">
                  <Link
                    href="/dashboard/ai-menu"
                    className="with-icon nav-link collapsed"
                    onClick={(e) => {
                      verificationStateRouter(e)
                    }}
                  >
                    <RiRobot2Line className="text-success" />
                    <span>IA Cardápio</span>
                  </Link>
                </li>
              )}
            </>
          )}
        {(user?.controls?.paymentInfo?.gateway
          ? user.controls.paymentInfo?.subscription?.status === 'active'
          : true) && (
          <li className="nav-item">
            <Link
              href="/dashboard/profile"
              className="with-icon nav-link collapsed"
              onClick={(e) => {
                verificationStateRouter(e)
              }}
            >
              <BsPerson />
              <span>{t('my_profile')}</span>
            </Link>
          </li>
        )}
        {profile.id &&
          ((profile.address.street && profile.taxDelivery.length > 0) ||
            plansCategory.every((plan) => plan === 'table')) && (
            <>
              <li className="nav-item">
                <Link
                  href="/dashboard/motoboys"
                  className="with-icon nav-link collapsed"
                  onClick={(e) => {
                    // verificationStateRouter(e);
                  }}
                >
                  <FaMotorcycle />
                  <span>{t('delivery_drivers')}</span>
                </Link>
              </li>
              {plansCategory.some((plan) => plan === 'table') &&
                !('isElectron' in window) && (
                  <li className="nav-item">
                    <Link
                      href={`${process.env.NEXT_PUBLIC_WHATSMENU_BASE_URL}/${profile.slug}/mesas`}
                      className="with-icon nav-link collapsed"
                      onClick={(e) => {
                        if (
                          bartenders.filter(
                            (bartender: any) => !bartender.deleted_at
                          ).length
                        ) {
                          verificationStateRouter(e)
                        } else {
                          handleConfirmModal({
                            title: t('no_active_waiter'),
                            message: t('message_register_waitr_enable'),
                            confirmButton: t('register_waiter'),
                            actionConfirm: () => {
                              router.push(
                                '/dashboard/settings/table?bartender=true'
                              )
                            },
                          })
                        }
                      }}
                      target="_blank"
                    >
                      <GiRoundTable />
                      <span>{t('waiter')}</span>
                    </Link>
                  </li>
                )}
              {!('isElectron' in window) && (
                <li className="nav-item">
                  <Link
                    href={`${process.env.NEXT_PUBLIC_WHATSMENU_BASE_URL}/${profile.slug}/pdv`}
                    className="with-icon nav-link collapsed"
                    onClick={(e) => {
                      verificationStateRouter(e)
                    }}
                    target="_blank"
                  >
                    <FaCashRegister />
                    <span>{i18n.t('pos')}</span>
                  </Link>
                </li>
              )}
              {!plansCategory.every((plan) => plan === 'table') && (
                <li className="nav-item">
                  <Link
                    href="/dashboard/cupons"
                    className="with-icon nav-link collapsed"
                    onClick={(e) => {
                      verificationStateRouter(e)
                    }}
                  >
                    <IoTicketOutline />
                    <span>{t('coupons')}</span>
                  </Link>
                </li>
              )}
              <li className="nav-item">
                <Link
                  href="/dashboard/discount"
                  className="with-icon nav-link collapsed"
                  onClick={(e) => {
                    verificationStateRouter(e)
                  }}
                >
                  <BsCashCoin />
                  <span>{t('progressive_discount')}</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  href="/dashboard/socialmedia"
                  className="with-icon nav-link collapsed"
                  onClick={(e) => {
                    verificationStateRouter(e)
                  }}
                >
                  <BsMegaphone />
                  <span>{t('social_media')}</span>
                </Link>
              </li>

              <li className="nav-item">
                <Accordion id="relatorio-nav" defaultActiveKey="0" flush>
                  <Accordion.Header
                    as="a"
                    bsPrefix="with-icon nav-link collapsed"
                    style={{ color: 'white !important' }}
                  >
                    <BsClipboardData />
                    <span>{i18n.t('reports')}</span>
                  </Accordion.Header>
                  <Accordion.Body
                    style={{ marginLeft: '1.2rem', color: 'white' }}
                  >
                    <Link
                      href="/dashboard/report/finance"
                      className="with-icon nav-link collapsed"
                      onClick={(e) => {
                        verificationStateRouter(e)
                      }}
                    >
                      <FaMoneyBillWave />
                      <span className="text-nowrap">
                        {i18n.t('financial_report')}
                      </span>
                    </Link>
                    {i18n.language === 'pt-BR' && (
                      <>
                        <Link
                          href={
                            !isNFCe
                              ? '/dashboard/integrations/grovenfe'
                              : '/dashboard/report/nfce'
                          }
                          className="with-icon nav-link collapsed"
                          onClick={(e) => {
                            verificationStateRouter(e)
                          }}
                        >
                          <BsFillFileEarmarkTextFill />
                          <span className="text-nowrap">Relatório NFCe</span>
                        </Link>
                      </>
                    )}
                    <Link
                      href="/dashboard/report/daily"
                      className="with-icon nav-link collapsed"
                      onClick={(e) => {
                        verificationStateRouter(e)
                      }}
                    >
                      <BsCalendarEvent />
                      <span>{i18n.t('daily_orders')}</span>
                    </Link>
                    <Link
                      href="/dashboard/report/monthly"
                      className="with-icon nav-link collapsed"
                      onClick={(e) => {
                        verificationStateRouter(e)
                      }}
                    >
                      <BsCalendar3 />
                      <span>{i18n.t('monthly_orders')}</span>
                    </Link>
                    <Link
                      href="/dashboard/report/cashier"
                      className="with-icon nav-link collapsed"
                      onClick={(e) => {
                        // verificationStateRouter(e);
                      }}
                    >
                      <span className="with-icon">
                        <FaCashRegister />
                        <span>{i18n.t('cash_register_closing')}</span>
                      </span>
                    </Link>
                    <Link
                      href="/dashboard/report/bestSellers"
                      className="with-icon nav-link collapsed"
                      onClick={(e) => {
                        verificationStateRouter(e)
                      }}
                    >
                      <FaMedal />
                      <span>{i18n.t('best_sellers')}</span>
                    </Link>
                    <Link
                      href="/dashboard/report/cmv"
                      className="with-icon nav-link collapsed"
                    >
                      <FaChartPie />
                      <span>Relatório CMV</span>
                    </Link>
                    <Link
                      href="/dashboard/report/motoboys"
                      className="with-icon nav-link collapsed"
                    >
                      <FaMotorcycle />
                      <span>{i18n.t('delivery_drivers')}</span>
                    </Link>
                    <Link
                      href="/dashboard/report/client"
                      className="with-icon nav-link collapsed"
                      onClick={(e) => {
                        verificationStateRouter(e)
                      }}
                    >
                      <FaList />
                      <span>{i18n.t('customer_report_side')}</span>
                    </Link>
                  </Accordion.Body>
                </Accordion>
              </li>
            </>
          )}
        {!user?.controls?.hideInoicesMenu && (
          <li className="nav-item">
            <Link
              href="/dashboard/invoices"
              className={`with-icon nav-link collapsed ${invoicePending?.invoices.length ? `pulse-${invoiceAlert}` : ''}`}
              onClick={(e) => {
                verificationStateRouter(e)
                window.innerWidth < 1020 && setShowSidebar(false)
              }}
            >
              <FaBarcode />
              <span className="d-flex justify-content-between align-items-center w-100">
                {i18n.t('invoices')}
              </span>
            </Link>
          </li>
        )}
        {/* <li className="nav-item">
          <Link
            href="/dashboard/invoiceGrove"
            className={`with-icon nav-link collapsed`}
            onClick={(e) => {
              verificationStateRouter(e)
              window.innerWidth < 1020 && setShowSidebar(false)
            }}
          >
            <FaBarcode />
            <span className="d-flex justify-content-between align-items-center w-100">
              {t('invoices')}
            </span>
          </Link>
        </li>
        {/* <li className="nav-item">
          <Link
            href="/dashboard/invoiceGrove"
            className={`with-icon nav-link collapsed`}
            onClick={(e) => {
              verificationStateRouter(e)
              window.innerWidth < 1020 && setShowSidebar(false)
            }}
          >
            <FaBarcode />
            <span className="d-flex justify-content-between align-items-center w-100">
              FaturasNFe
            </span>
          </Link>
        </li> */}

        {profile.id &&
          ((profile.address.street && profile.taxDelivery.length > 0) ||
            plansCategory.every((plan) => plan === 'table')) && (
            <li className="nav-item">
              <Accordion id="config-nav" defaultActiveKey="0" flush>
                <Accordion.Header
                  as="a"
                  bsPrefix="with-icon nav-link collapsed"
                  style={{ color: 'white !important' }}
                >
                  <ImCogs />
                  <span>{i18n.t('settings')}</span>
                </Accordion.Header>
                <Accordion.Body
                  style={{ marginLeft: '1.2rem', color: 'white' }}
                >
                  <Link
                    href="/dashboard/settings"
                    className="with-icon nav-link collapsed"
                    onClick={(e) => {
                      verificationStateRouter(e)
                      window.innerWidth < 1020 && setShowSidebar(false)
                    }}
                  >
                    <BsGear />
                    <span>{i18n.t('general')}</span>
                  </Link>
                  {plansCategory.includes('package') && (
                    <Link
                      href="/dashboard/settings/package"
                      className="with-icon nav-link collapsed"
                      onClick={(e) => {
                        verificationStateRouter(e)
                        window.innerWidth < 1020 && setShowSidebar(false)
                      }}
                    >
                      {profile.options.package.label2 ? (
                        <BsCalendar2Date />
                      ) : (
                        <GoPackage />
                      )}
                      <span>{textPackage(profile.options.package.label2)}</span>
                    </Link>
                  )}
                  {plansCategory.includes('table') && (
                    <>
                      <Link
                        href="/dashboard/settings/table"
                        className="with-icon nav-link collapsed"
                        onClick={(e) => {
                          verificationStateRouter(e)
                          window.innerWidth < 1020 && setShowSidebar(false)
                        }}
                      >
                        <RiRestaurantLine />
                        <span>{i18n.t('tables')}</span>
                      </Link>
                    </>
                  )}
                  <Link
                    href="/dashboard/settings/cashiers"
                    className="with-icon nav-link collapsed"
                    onClick={(e) => {
                      verificationStateRouter(e)
                      window.innerWidth < 1020 && setShowSidebar(false)
                    }}
                  >
                    <FaCashRegister />
                    <span>{i18n.t('cash_registers')}</span>
                  </Link>
                  <Link
                    href="/dashboard/settings/domains"
                    className="with-icon nav-link collapsed"
                    onClick={(e) => {
                      verificationStateRouter(e)
                      window.innerWidth < 1020 && setShowSidebar(false)
                    }}
                  >
                    <BsFillCloudyFill />
                    <span>{i18n.t('domains')}</span>
                  </Link>
                  {user?.controls?.paymentInfo?.gateway && (
                    <Link
                      href="/dashboard/settings/payment-details"
                      className="with-icon nav-link collapsed"
                      onClick={(e) => {
                        verificationStateRouter(e)
                        window.innerWidth < 1020 && setShowSidebar(false)
                      }}
                    >
                      <BiDollar />
                      <span>{i18n.t('payment_methods')}</span>
                    </Link>
                  )}
                </Accordion.Body>
              </Accordion>
            </li>
          )}
        {/*
        <li className="nav-item">
          <Link
            className="with-icon nav-link collapsed"
            target="_blank"
            rel="noreferrer"
            href="https://whatsmenu.com.br/videoaulas"
          >
            <BsYoutube fontVariant={"white"} />
            <span>Vídeos de Ajuda</span>
          </Link>
        </li> */}
        {(user?.controls?.type === 'adm' ||
          user?.controls?.type === 'manager' ||
          user?.controls?.type === 'seller' ||
          user?.controls?.type === 'support') && (
          <>
            <li className="nav-heading">{t('administrator')}</li>

            {/* <li className="nav-item">
              <Link href="/adm/release-block">
                <Link className="with-icon nav-link collapsed"
                  onClick={e => {
                    verificationStateRouter(e);
                    window.innerWidth < 1020 && setShowSidebar(false);
                  }}>
                  <BsUnlock />
                  <span>Bloq./Desbl.</span>
                </Link>
              </Link>
            </li> */}

            <li className="nav-item">
              <Link
                href="/adm/products"
                className="with-icon nav-link collapsed"
                onClick={(e) => {
                  verificationStateRouter(e)
                }}
              >
                <FaFileInvoiceDollar />
                <span>{t('products')}</span>
              </Link>
            </li>

            <li className="nav-item">
              <Link
                href="/adm/client"
                className="with-icon nav-link collapsed"
                onClick={(e) => {
                  verificationStateRouter(e)
                }}
              >
                <BsPerson />
                <span>{t('client')}</span>
              </Link>
            </li>

            {user?.controls?.type === 'adm' && (
              <li className="nav-item">
                <Link
                  href="/adm/support-queues"
                  className="with-icon nav-link collapsed"
                  onClick={(e) => {
                    verificationStateRouter(e)
                  }}
                >
                  <BiSupport />
                  <span>Filas de suporte</span>
                </Link>
              </li>
            )}

            {user?.controls?.type === 'adm' && (
              <li className="nav-item">
                <Link
                  href="/adm/campaigns"
                  className="with-icon nav-link collapsed"
                  onClick={(e) => {
                    verificationStateRouter(e)
                  }}
                >
                  <BsMegaphone />
                  <span>Campanhas</span>
                </Link>
              </li>
            )}

            {user?.controls?.type === 'adm' && (
              <li className="nav-item">
                <Link
                  href="/adm/webinar"
                  className="with-icon nav-link collapsed"
                  onClick={(e) => {
                    verificationStateRouter(e)
                  }}
                >
                  <BsPeople />
                  <span>Inscritos Webinar</span>
                </Link>
              </li>
            )}

            <li className="nav-item">
              <Accordion id="relatorioAdm-nav" defaultActiveKey="0" flush>
                <Accordion.Header
                  as="a"
                  bsPrefix="with-icon nav-link collapsed"
                  style={{ color: 'white !important' }}
                >
                  <BsClipboardData />
                  <span>{t('reports')}</span>
                </Accordion.Header>
                <Accordion.Body
                  className="p-0"
                  style={{ marginLeft: '1.2rem', color: 'white' }}
                >
                  <Link
                    href="/adm/report/support"
                    className="with-icon nav-link collapsed"
                    onClick={(e) => {
                      verificationStateRouter(e)
                    }}
                  >
                    <BsBullseye />
                    <span>{t('support_bonus')}</span>
                  </Link>
                  <Link
                    href="/adm/report/registers"
                    className="with-icon nav-link collapsed"
                    onClick={(e) => {
                      verificationStateRouter(e)
                    }}
                  >
                    <BsFilePerson />
                    <span>{t('salesperson_report')}</span>
                  </Link>
                  {user?.controls?.type === 'adm' && (
                    <Link
                      href="/adm/report/financial"
                      className="with-icon nav-link collapsed"
                      onClick={(e) => {
                        verificationStateRouter(e)
                      }}
                    >
                      <BsCalendar3 />
                      <span>{t('annual_report')}</span>
                    </Link>
                  )}
                  <Link
                    href="/adm/report/implemented-reports"
                    className="with-icon nav-link collapsed"
                    onClick={(e) => {
                      verificationStateRouter(e)
                    }}
                  >
                    <BsCalendar3 />
                    <span>Relátorio Implantações</span>
                  </Link>
                  <Link
                    href="/adm/report/leads"
                    className="with-icon nav-link collapsed"
                    onClick={(e) => {
                      verificationStateRouter(e)
                    }}
                  >
                    <BsFunnel />
                    <span>Leads</span>
                  </Link>
                </Accordion.Body>
              </Accordion>
            </li>

            {/* <li className="nav-item">
              <Link href="/adm/report/users/card">
                <Link className="with-icon nav-link collapsed" href="ativos.html"
                  onClick={e => {
                    verificationStateRouter(e);
                  }}>
                  <BsCardText />
                  <span>Ativos Cartão</span>
                </Link>
              </Link>
            </li> */}
          </>
        )}
        {hasUpdates && (
          <li
            className="nav-item"
            style={{
              order: isNewUpdate ? -1 : undefined,
            }}
          >
            <Link
              href="/dashboard/updates"
              className={`with-icon nav-link collapsed ${
                isNewUpdate ? `pulse-${updateAlert}` : ''
              } first-item`}
              onClick={handleNewsClick}
            >
              {/* Ícone dinâmico: Estrelas cintilantes se update for novo, documento/jornal se update já foi visto */}
              {isNewUpdate ? (
                <BsStars className={`text-warning`} />
              ) : (
                <BsFillFileEarmarkArrowUpFill />
              )}

              <span>{i18n.t('news')}</span>
            </Link>
          </li>
        )}
        <li className="nav-item">
          <Link
            href="/auth/login"
            className="with-icon nav-link collapsed"
            onClick={(e) => {
              if ('ReactNativeWebView' in window) {
                ;(window as any).ReactNativeWebView.postMessage('logout')
              }
              verificationStateRouter(e, true)
              e.preventDefault()
            }}
          >
            <BsBoxArrowRight />
            <span>{i18n.t('logout')}</span>
          </Link>
        </li>
        <li className="nav-item">
          <Link href="/auth/login">
            <Button
              className="with-icon nav-link collapsed w-100 border-0"
              style={{ outline: 0, boxShadow: 'none' }}
              onClick={(e) => {
                setShowNewFeatureModal(true)
              }}
            >
              <MdVideoSettings />
              <span>{i18n.t('pos_videos')}</span>
            </Button>
          </Link>
        </li>
      </ul>

      <div className="sidebar-support rounded-3 bg-primary mb-3 mt-5 p-3 text-white">
        <Link
          className="link support-message"
          href={IS_TAWKTO_ENABLED ? '#' : SUPPORT_WHATSAPP_URL}
          target={IS_TAWKTO_ENABLED ? undefined : '_blank'}
          onClick={IS_TAWKTO_ENABLED ? toggleTawkTo : undefined}
        >
          <Image
            className="img-fluid"
            src="/images/suporte.png"
            alt="Suporte WhatsMenu"
            layout="fill"
            objectFit="contain"
          />
        </Link>
        <p className="mb-0 mt-2">
          {t('support')}:
        </p>
        <p className="mb-0">
          {t('everyday')}
        </p>
        <p className="mb-0">
          24h
        </p>
        <p className="mb-0 mt-2">
          {t('technical_support')}:
        </p>
        <p className="mb-0">
          {t('support_hours')}: {formattedHours}
        </p>
      </div>
    </>
  )
}
